import { Button, Popup, Table } from '@mairistem/composants';
import type { Exercice } from '@mairistem/comptabilite';
import { formatMontant } from '@mairistem/finances-utils';
import React from 'react';
import BLOCAGE_SAISIE_IMPUTATION from '../../../constants/autorisation/blocageSaisieImputation';
import type Financement from '../../../interfaces/common/financement';
import type ProImputation from '../../../interfaces/common/proImputation';
import {
	getBlocageSaisieImputation,
	getExerciceSaisie,
	getLibelleBlocageSaisieImputation,
	isExerciceValide,
} from '../../../utils/autorisation';
import ProImputationItem from './ProImputationItem';

interface ProImputationListProps {
	decision: number;
	exercices: Exercice[];
	exercicesGestionTva: Set<number>;
	exercicesReportFonc: Set<number>;
	exercicesSansEspace: Set<number>;
	financement: Financement;
	identifiantService: number | null;
	/** La gestion par service est disponible et un service est selectionne */
	isGestionParService: boolean;
	onAddProImputation: () => void;
	onChangeProImputation: (
		data: Partial<ProImputation>,
		identifiantProImputation: number | string,
	) => void;
	onRemoveProImputation: (identifiantProImputation: number | string) => void;
}

const getRemainingMontantText = (montant: number) =>
	`(Restant: ${formatMontant(montant)})`;

const ProImputationList = ({
	decision,
	exercices,
	exercicesGestionTva,
	exercicesReportFonc,
	exercicesSansEspace,
	financement,
	identifiantService,
	isGestionParService,
	onAddProImputation,
	onChangeProImputation,
	onRemoveProImputation,
}: ProImputationListProps) => {
	const exerciceHasGestionTva = React.useMemo(
		() => exercicesGestionTva.has(financement.exercice),
		[exercicesGestionTva, financement.exercice],
	);

	const exerciceHasReportFonc = React.useMemo(
		() => exercicesReportFonc.has(financement.exercice),
		[exercicesReportFonc, financement.exercice],
	);

	// Exercice sur lequel les imputations sont saisies (le dernier exercice si celui du financement n'existe pas encore)
	const exerciceSaisie = React.useMemo(
		() => getExerciceSaisie(exercices, financement.exercice),
		[exercices, financement.exercice],
	);

	// En gestion par service, aucune ligne ne peut etre ajoutee tant que l'exercice n'est pas saisi
	// Sinon, c'est la ligne d'imputation qui indique que l'exercice n'est pas saisi
	const blocageSaisie = React.useMemo(() => {
		if (!isExerciceValide(financement.exercice))
			return isGestionParService
				? BLOCAGE_SAISIE_IMPUTATION.EXERCICE_NON_SAISI
				: null;

		return getBlocageSaisieImputation(
			exerciceSaisie,
			identifiantService,
			exercicesSansEspace,
		);
	}, [
		exerciceSaisie,
		exercicesSansEspace,
		financement.exercice,
		identifiantService,
		isGestionParService,
	]);

	const montantsAImputer = React.useMemo(
		() =>
			financement.proImputations.reduce(
				(prev, curr) => ({
					montant: prev.montant - curr.montant,
					rarN: prev.rarN - curr.rarN,
					rarN1: prev.rarN1 - curr.rarN1,
					revision: prev.revision - curr.revision,
				}),
				{
					montant: financement.montant,
					rarN: financement.rarN,
					rarN1: financement.rarN1,
					revision: financement.revision,
				},
			),
		[
			financement.montant,
			financement.proImputations,
			financement.rarN,
			financement.rarN1,
			financement.revision,
		],
	);

	return (
		<Table singleLine>
			<Table.Header>
				<Table.Row>
					<Table.HeaderCell content="Ligne budgétaire" width={5} />
					<Table.HeaderCell
						content={`Crédit initial ${getRemainingMontantText(montantsAImputer.montant)}`}
						width={2}
					/>
					<Table.HeaderCell
						content={`Rééchelonnement ${getRemainingMontantText(montantsAImputer.revision)}`}
						width={2}
					/>
					<Table.HeaderCell
						content={`RAR ${financement.exercice ? financement.exercice : ''} ${getRemainingMontantText(montantsAImputer.rarN)}`}
						textAlign="right"
						width={2}
					/>
					<Table.HeaderCell
						content={`RAR ${financement.exercice ? financement.exercice - 1 : ''} ${getRemainingMontantText(montantsAImputer.rarN1)}`}
						textAlign="right"
						width={2}
					/>
					<Table.HeaderCell content="Engagé" textAlign="right" width={1} />
					<Table.HeaderCell content="Mandaté" textAlign="right" width={1} />
					<Table.HeaderCell content="Actions" textAlign="center" width={1} />
				</Table.Row>
			</Table.Header>
			<Table.Body>
				{financement.proImputations?.map((proImputation) => (
					<ProImputationItem
						decision={decision}
						exercice={financement.exercice}
						exerciceHasGestionTva={exerciceHasGestionTva}
						exerciceHasReportFonc={exerciceHasReportFonc}
						blocageSaisie={blocageSaisie}
						exerciceSaisie={exerciceSaisie}
						identifiantService={identifiantService}
						key={proImputation.identifiant}
						onChangeProImputation={(data) =>
							onChangeProImputation(data, proImputation.identifiant)
						}
						onRemoveProImputation={() =>
							onRemoveProImputation(proImputation.identifiant)
						}
						proImputation={proImputation}
						sens={financement.sens}
					/>
				))}
				<Table.Row>
					<Table.Cell colSpan={7} textAlign="center">
						<Popup
							content={
								blocageSaisie &&
								getLibelleBlocageSaisieImputation(blocageSaisie, exerciceSaisie)
							}
							disabled={!blocageSaisie}
							position="top center"
							trigger={
								// Un bouton desactive ne declenche pas d'evenement souris : le span porte le survol
								<span>
									<Button
										content="Ajouter une ligne"
										data-testid={`addProImputationButton${financement.identifiant}`}
										disabled={!!blocageSaisie}
										icon="plus"
										onClick={onAddProImputation}
										primary
										size="tiny"
									/>
								</span>
							}
						/>
					</Table.Cell>
				</Table.Row>
			</Table.Body>
		</Table>
	);
};

export default ProImputationList;
